import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import whiteTextLogo from "../../assets/eldoo-logo-white-text.png";
import useBodyScrollLock from "../../hooks/useBodyScrollLock";
import "../../styles/layout/Footer.css";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void> | void;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform?: string }>;
}

type InstallHelpMode = "refresh" | "installed";

const IconEmail = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 6h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Zm0 2v.3l8 5.2 8-5.2V8H4Zm16 8V10l-7.4 4.8a1 1 0 0 1-1.2 0L4 10v6h16Z" />
  </svg>
);

const IconPhone = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6.6 2.8c.5-.5 1.2-.6 1.8-.3l3 1.4c.6.3.9 1 .8 1.6l-.5 2.4a1 1 0 0 0 .3.9l3.2 3.2a1 1 0 0 0 .9.3l2.4-.5c.7-.1 1.3.2 1.6.8l1.4 3c.3.6.2 1.3-.3 1.8l-1.4 1.4a3 3 0 0 1-2.7.8c-2.6-.6-5.4-2.3-8.2-5.1-2.8-2.8-4.5-5.6-5.1-8.2a3 3 0 0 1 .8-2.7L6.6 2.8Z" />
  </svg>
);

const IconInstagram = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M7 3h10a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4Zm0 2a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H7Zm11 1.2a1.2 1.2 0 1 1 0 2.4 1.2 1.2 0 0 1 0-2.4ZM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z" />
  </svg>
);

const IconFacebook = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M14 3a4 4 0 0 0-4 4v2H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3V3h-4Z" />
  </svg>
);

const IconLinkedIn = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6.9 6.5A1.9 1.9 0 1 1 7 2.7a1.9 1.9 0 0 1-.1 3.8ZM5 8.5h4v13H5v-13Zm6.5 0h3.8v1.8h.1c.5-1 1.8-2.1 3.7-2.1 4 0 4.7 2.6 4.7 6v7.3h-4v-6.5c0-1.6 0-3.7-2.2-3.7s-2.5 1.7-2.5 3.6v6.6h-4v-13Z" />
  </svg>
);

const IconSupport = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 2a8 8 0 0 0-8 8v4a3 3 0 0 0 3 3h1v-6H5v-1a7 7 0 0 1 14 0v1h-3v6h1a3 3 0 0 0 3-3v-4a8 8 0 0 0-8-8Zm-5 10h1v4h-1a1 1 0 0 1-1-1v-2a1 1 0 0 1 1-1Zm10 0h1a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-1v-4Z" />
  </svg>
);

const Footer: React.FC = () => {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installHelpOpen, setInstallHelpOpen] = useState(false);
  const [installHelpMode, setInstallHelpMode] = useState<InstallHelpMode>("refresh");

  useBodyScrollLock(installHelpOpen);

  const isStandaloneDisplay = () => {
    if (typeof window === "undefined") {
      return false;
    }

    return window.matchMedia?.("(display-mode: standalone)").matches || (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  };

  const isKnownInstalled = () =>
    isStandaloneDisplay() || (typeof localStorage !== "undefined" && localStorage.getItem("eldoo-app-installed") === "true");

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      localStorage.setItem("eldoo-app-installed", "true");
      setInstallPrompt(null);
      setInstallHelpMode("installed");
      setInstallHelpOpen(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleDownloadWebApp = async () => {
    if (isKnownInstalled() && !installPrompt) {
      setInstallHelpMode("installed");
      setInstallHelpOpen(true);
      return;
    }

    if (installPrompt) {
      await installPrompt.prompt();
      await installPrompt.userChoice.catch(() => null);
      setInstallPrompt(null);
      return;
    }

    setInstallHelpMode("refresh");
    setInstallHelpOpen(true);
  };

  const handleReinstallWebApp = async () => {
    localStorage.removeItem("eldoo-app-installed");
    if (installPrompt) {
      await handleDownloadWebApp();
      return;
    }
    window.location.reload();
  };

  return (
    <footer className="site-footer">
      <div className="shell footer-brand">
        <Link to="/" className="footer-brand__logo" aria-label="Go to home">
          <img src={whiteTextLogo} alt="Eldoo" />
        </Link>
        <p className="footer-brand__title">Shop Smarter. Save Bigger. Live Better.</p>
        <p className="footer-brand__subtitle">Everything you need, delivered with care.</p>
        <button
          type="button"
          className="footer-extension__button"
          onClick={handleDownloadWebApp}
          aria-label="Download app"
        >
          <span className="footer-extension__button-icon" aria-hidden="true">
            <IconSupport />
          </span>
          <span>Download App</span>
        </button>
      </div>
      <div className="shell footer-links">
        <section className="footer-links__section footer-links__section--social" aria-label="Social links">
          <h2>Social Links</h2>
          <div className="footer-links__list footer-links__list--compact">
            <a href="https://www.instagram.com/eldoo.in/" target="_blank" rel="noreferrer" aria-label="Instagram">
              <IconInstagram />
              <span>Instagram</span>
            </a>
            <a href="https://www.facebook.com/" target="_blank" rel="noreferrer" aria-label="Facebook">
              <IconFacebook />
              <span>Facebook</span>
            </a>
            <a href="https://www.linkedin.com/" target="_blank" rel="noreferrer" aria-label="LinkedIn">
              <IconLinkedIn />
              <span>LinkedIn</span>
            </a>
          </div>
        </section>

        <section className="footer-links__section" aria-label="Call us">
          <h2>Call Us</h2>
          <div className="footer-links__list footer-links__list--compact">
            <a href="tel:+919347023242" aria-label="Call us">
              <IconPhone />
              <span>Call</span>
            </a>
            <a href="mailto:eldoo.service@gmail.com" aria-label="Mail us">
              <IconEmail />
              <span>Email</span>
            </a>
            <Link to="/contact" aria-label="Support">
              <IconSupport />
              <span>Support</span>
            </Link>
          </div>
        </section>

        <section className="footer-links__section" aria-label="Legal">
          <h2>Legal</h2>
          <div className="footer-links__list footer-links__list--legal">
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Service</Link>
            <Link to="/returns">Refund & Cancellation</Link>
          </div>
        </section>
      </div>
      <div className="footer-divider" aria-hidden="true" />
      <p className="site-footer__copyright">Copyright © Eldoo. All rights reserved.</p>

      {installHelpOpen ? (
        <div className="footer-install-modal" role="dialog" aria-modal="true" aria-label="Install app help">
          <div className="footer-install-modal__backdrop" aria-hidden="true" />
          <div className="footer-install-modal__dialog">
            <button
              type="button"
              className="footer-install-modal__close"
              onClick={() => setInstallHelpOpen(false)}
              aria-label="Close install help"
            >
              ×
            </button>
            <h2>{installHelpMode === "installed" ? "Already installed" : "Refresh and click again"}</h2>
            <p className="footer-install-modal__message">
              {installHelpMode === "installed"
                ? "Eldoo app is already installed on this device."
                : "Refresh this page, then click Download App again to see the install option."}
            </p>
            <div className="footer-install-modal__actions">
              <button
                type="button"
                className="footer-install-modal__primary"
                onClick={installHelpMode === "installed" ? handleReinstallWebApp : () => window.location.reload()}
              >
                {installHelpMode === "installed" ? "Re-install" : "Refresh"}
              </button>
              {installHelpMode === "installed" ? (
                <button
                  type="button"
                  className="footer-install-modal__secondary"
                  onClick={handleReinstallWebApp}
                >
                  Download Again
                </button>
              ) : null}
              <button type="button" className="footer-install-modal__secondary" onClick={() => setInstallHelpOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </footer>
  );
};

export default Footer;
